import { useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import {
  Day,
  EditorOperation,
  ChangeClassroomInputType,
  ChangeInstructorInputType,
  ChangeIntervalsInputType,
  ChangeBlocksInputType,
  EditorChangesetInput,
  Mutation,
  SessionCrudResult,
} from "@models/ISchema";
import { useSettingsStore } from "@context/useSettingsStore";
import { isGroupDropDateEnabled, unassignedInstructorsInput } from "@utils/unassignedInstructors";
import { savableSessionsToCreate } from "../utils/context";
import { buildUnassignedInstructorIds } from "../utils/unassignedInstructorIds";
import { IParams } from "@models/IParams";
import { AppContext } from "../context/EditSessionsContext";
import { sessionsCrud } from "../graphql/sessionsCrud.mutation";
import { BlockRanges, EditedSession } from "../context/formData.reducer";

interface MutationProps {
  dryRun: boolean;
  skipValidations: boolean;
}

interface SessionsCrudState {
  data: SessionCrudResult | null;
  error: boolean;
}

type SubmitSessionsCrud = () => Promise<SessionCrudResult | null>;

const useSessionsCrudMutation = (props: MutationProps): [SessionsCrudState, SubmitSessionsCrud] => {
  const { dryRun, skipValidations } = props;
  const { scenario, origin }: IParams = useParams();
  const { state } = useContext(AppContext);
  const isDropDateEnabled = useSettingsStore(settings =>
    isGroupDropDateEnabled(settings.instructorLinkTags),
  );

  const [sessionsCrudMutation] = useMutation<Mutation>(sessionsCrud, {});
  const [result, setResult] = useState<SessionCrudResult | null>(null);
  const [error, setError] = useState(false);

  const blocksByHours = (blocks: BlockRanges): ChangeBlocksInputType => {
    const day = blocks?.day?.toLowerCase()?.replace(/^\w/, (c: string) => c.toUpperCase());
    return {
      op: EditorOperation.Change,
      day: Day[day],
      startTime: blocks?.startTime,
      endTime: blocks?.endTime,
    };
  };

  const changeClassroom = (session: EditedSession): ChangeClassroomInputType => ({
    op: EditorOperation.Change,
    classroomIds: (session?.classrooms ?? []).map(classroom => classroom?.id),
  });

  const changeInstructor = (session: EditedSession): ChangeInstructorInputType => ({
    op: EditorOperation.Change,
    instructorIds: (session?.instructors ?? []).map(instructor => instructor?.id),
  });

  const changeIntervals = (session: EditedSession): ChangeIntervalsInputType => ({
    op: EditorOperation.Change,
    intervalIds: (session?.intervals ?? [])
      .filter(interval => interval?.checked ?? false)
      .map(interval => interval?.id),
    uniqueInterval: false,
  });

  const buildSessionChangeset =
    (isCreation: boolean) =>
    (session: EditedSession): EditorChangesetInput => {
      const blocks =
        session?.blocks?.selected === "hours"
          ? { changeBlocks: blocksByHours(session?.blocks) }
          : { noScheduleEventsCount: session?.blocks?.blocks };

      return {
        sessionId: session?.id,
        sectionId: isCreation ? session?.section?.id : null,
        ...blocks,
        changeClassroom: changeClassroom(session),
        changeInstructor: changeInstructor(session),
        changeIntervals: changeIntervals(session),
      };
    };

  const setVariables = () => {
    const sessionsToDelete = state?.form?.sessionsToDelete ?? {};
    const { updates, creates } = (state?.form?.savedSessions ?? [])
      .filter(
        session => !Object.prototype.hasOwnProperty.call(sessionsToDelete, session?.id ?? "-"),
      )
      .reduce<{ updates: EditedSession[]; creates: EditedSession[] }>(
        (acc, session) => {
          if (session?.isNew) acc.creates.push(session);
          else acc.updates.push(session);
          return acc;
        },
        { updates: [], creates: [] },
      );

    // Add the "cloned" sessions to the "creates" array
    creates.push(...savableSessionsToCreate(state?.form?.sessionsToCreate));

    return {
      dryRun,
      skipValidations,
      linkId: state?.link?.info?.id,
      changesets: {
        updates: updates.map(buildSessionChangeset(false)),
        creates: creates.map(buildSessionChangeset(true)),
        deletes: {
          sections: [],
          sessions: Object.keys(state?.form?.sessionsToDelete ?? {}),
        },
      },
      ...unassignedInstructorsInput(
        buildUnassignedInstructorIds([...updates, ...creates], state?.link?.sections ?? []),
        isDropDateEnabled,
      ),
    };
  };

  const submitSessionsCrud = async () => {
    setError(false);
    try {
      const variables = {
        scenarioId: scenario,
        originId: origin,
        input: setVariables(),
      };
      const { data } = await sessionsCrudMutation({ variables });
      const response = data?.cube?.sessionsCrud ?? null;
      setResult(response);
      return response;
    } catch (error) {
      setError(true);
      console.error("[SessionsCrud]: ", error);
      return null;
    }
  };

  return [{ data: result, error }, submitSessionsCrud];
};

export default useSessionsCrudMutation;
